/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). Dependent-demand math for the
 * BoM forecast: explode finished-goods builds through the bills of materials,
 * net against stock, open POs and safety stock, offset by lead time and round
 * to the order rules. Pure functions, no React, no dates: time is expressed as
 * days from today so the results are identical on any day.
 */
import {
  ALTERNATE_EFFECTIVITY_DAYS,
  BOM_LINES,
  BUCKET_COUNT,
  BUCKET_START_DAYS,
  COMPONENTS,
  FG_SKUS,
  FINISHED_GOODS,
  RELEASE_WINDOW_DAYS,
  RISK_WINDOW_BUCKETS,
} from './bom-data';
import type {
  BomComponent,
  BomLine,
  ComponentAlternate,
  ComponentSku,
  FgSku,
} from './bom-data';
import type { SiteId, SupplierId } from '../../data/types';

export type FgForecast = Record<FgSku, number[]>;

/** The "+20% scenario" multiplier. */
export const SCENARIO_FACTOR = 1.2;

const DAYS_PER_WEEK = 7;

/** One value per panel and kit. */
export function fgRecord<T>(fn: (sku: FgSku) => T): Record<FgSku, T> {
  return {
    'PPC-MCC-480': fn('PPC-MCC-480'),
    'PPC-PDP-208': fn('PPC-PDP-208'),
    'KIT-VFD-50': fn('KIT-VFD-50'),
  };
}

export function baselineForecast(): FgForecast {
  return fgRecord((sku) => {
    const fg = FINISHED_GOODS.find((item) => item.sku === sku);
    return fg ? [...fg.forecast] : [];
  });
}

/** Multiply every bucket and round to whole builds. */
export function scaleForecast(
  forecast: FgForecast,
  factor: number,
): FgForecast {
  return fgRecord((sku) =>
    forecast[sku].map((units) => Math.round(units * factor)),
  );
}

/** Cell overrides keyed `${sku}:${bucketIndex}`. An override is an absolute value. */
export type ForecastOverrides = Record<string, number>;

export function overrideKey(sku: FgSku, bucket: number): string {
  return `${sku}:${bucket}`;
}

export function applyOverrides(
  forecast: FgForecast,
  overrides: ForecastOverrides,
): FgForecast {
  return fgRecord((sku) =>
    forecast[sku].map(
      (units, bucket) => overrides[overrideKey(sku, bucket)] ?? units,
    ),
  );
}

export function sumBuckets(values: number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

export function totalBuilds(forecast: FgForecast): number {
  return FG_SKUS.reduce((total, sku) => total + sumBuckets(forecast[sku]), 0);
}

/** Gross requirement per bucket: sum of finished goods builds times quantity per. */
export function grossRequirement(
  component: ComponentSku,
  forecast: FgForecast,
  lines: BomLine[] = BOM_LINES,
): number[] {
  const gross: number[] = Array.from({ length: BUCKET_COUNT }, () => 0);
  for (const line of lines) {
    if (line.component !== component) continue;
    for (let bucket = 0; bucket < BUCKET_COUNT; bucket += 1) {
      gross[bucket] += forecast[line.fg][bucket] * line.qtyPer;
    }
  }
  return gross;
}

/** Round a net requirement up to the minimum order quantity, then to the pack multiple. */
export function roundToOrder(
  net: number,
  moq: number,
  multiple: number,
): number {
  if (net <= 0) return 0;
  const atLeastMoq = Math.max(net, moq);
  return Math.ceil(atLeastMoq / multiple) * multiple;
}

export interface MrpInput {
  gross: number[];
  onHand: number;
  safetyStock: number;
  /** Open PO receipts per bucket that can be counted on. */
  receipts: number[];
  moq: number;
  multiple: number;
  /** Lead time in days for the order covering each bucket. */
  leadTimeDays: number[];
  /** Days from today to the first need of each bucket. */
  needDays: readonly number[];
}

export interface MrpBucket {
  gross: number;
  receipts: number;
  /** Projected on hand after receipts and demand, before any planned order. */
  projectedBefore: number;
  /** Shortfall against safety stock; 0 when covered. */
  net: number;
  /** Planned order quantity after MOQ and pack rounding. */
  plannedQty: number;
  /** Projected on hand after the planned order lands. */
  projectedAfter: number;
  needDay: number;
  /** Days from today to release the planned order; negative means already late. */
  releaseDay: number;
}

/**
 * Time-phased netting. Each bucket: projected = previous + receipts - gross.
 * The net requirement is the shortfall against safety stock, a planned order
 * covers it (rounded), and the planned order releases one lead time before the
 * first need in the bucket.
 */
export function runMrp(input: MrpInput): MrpBucket[] {
  const buckets: MrpBucket[] = [];
  let projected = input.onHand;
  for (let bucket = 0; bucket < input.gross.length; bucket += 1) {
    const gross = input.gross[bucket];
    const receipts = input.receipts[bucket] ?? 0;
    const projectedBefore = projected + receipts - gross;
    const net = Math.max(0, input.safetyStock - projectedBefore);
    const plannedQty = roundToOrder(net, input.moq, input.multiple);
    const needDay = input.needDays[bucket];
    projected = projectedBefore + plannedQty;
    buckets.push({
      gross,
      receipts,
      projectedBefore,
      net,
      plannedQty,
      projectedAfter: projected,
      needDay,
      releaseDay: needDay - input.leadTimeDays[bucket],
    });
  }
  return buckets;
}

export function isLate(releaseDay: number): boolean {
  return releaseDay < 0;
}

export function weeksToDays(weeks: number): number {
  return weeks * DAYS_PER_WEEK;
}

export interface Sourcing {
  /** Ordered part number: the alternate's when swapped. */
  sku: string;
  supplierId: SupplierId;
  unitPrice: number;
  leadTimeWeeks: number;
  isAlternate: boolean;
}

/** The qualified alternate that can replace a held primary source, if any. */
export function swapAlternate(
  component: BomComponent,
): ComponentAlternate | undefined {
  if (!component.hold) return undefined;
  return component.alternates.find((alt) => alt.status === 'qualified');
}

/** Who supplies the order that covers `bucket`, given whether Rev C is approved. */
export function sourcingFor(
  component: BomComponent,
  bucket: number,
  swapApproved: boolean,
): Sourcing {
  const alternate = swapAlternate(component);
  if (
    swapApproved &&
    alternate &&
    BUCKET_START_DAYS[bucket] <= ALTERNATE_EFFECTIVITY_DAYS
  ) {
    return {
      sku: alternate.sku,
      supplierId: alternate.supplierId,
      unitPrice: alternate.unitPrice,
      leadTimeWeeks: alternate.leadTimeWeeks,
      isAlternate: true,
    };
  }
  return {
    sku: component.sku,
    supplierId: component.supplierId,
    unitPrice: component.unitPrice,
    leadTimeWeeks: component.leadTimeWeeks,
    isAlternate: false,
  };
}

export interface PlannedOrder {
  id: string;
  componentSku: ComponentSku;
  /** Ordered part number (the alternate's when swapped). */
  sku: string;
  bucket: number;
  qty: number;
  uom: string;
  supplierId: SupplierId;
  shipTo: SiteId;
  unitPrice: number;
  value: number;
  needDay: number;
  releaseDay: number;
  late: boolean;
  isAlternate: boolean;
  /** Priced from a contract clause rather than a catalog price. */
  contractPriced: boolean;
}

export interface ComponentPlan {
  component: BomComponent;
  gross: number[];
  buckets: MrpBucket[];
  sourcing: Sourcing[];
  orders: PlannedOrder[];
  /** Total planned quantity across the six buckets. */
  plannedQty: number;
  plannedValue: number;
  /** Premium paid over the primary price on alternate-sourced orders. */
  alternatePremium: number;
  lateOrders: number;
  /** Unmet demand in the risk window with the held supply excluded. */
  windowShortfall: number;
  holdActive: boolean;
  atRisk: boolean;
  /** Why the component is at risk, in plain words. */
  riskReasons: string[];
}

export function planComponent(
  component: BomComponent,
  forecast: FgForecast,
  swapApproved: boolean,
): ComponentPlan {
  const gross = grossRequirement(component.sku, forecast);
  const sourcing = gross.map((_, bucket) =>
    sourcingFor(component, bucket, swapApproved),
  );
  const buckets = runMrp({
    gross,
    onHand: component.onHand,
    safetyStock: component.safetyStock,
    receipts: component.receipts,
    moq: component.moq,
    multiple: component.multiple,
    leadTimeDays: sourcing.map((source) => weeksToDays(source.leadTimeWeeks)),
    needDays: BUCKET_START_DAYS,
  });

  const orders: PlannedOrder[] = [];
  buckets.forEach((bucket, index) => {
    if (bucket.plannedQty <= 0) return;
    const source = sourcing[index];
    orders.push({
      id: `${component.sku}:${index}`,
      componentSku: component.sku,
      sku: source.sku,
      bucket: index,
      qty: bucket.plannedQty,
      uom: component.uom,
      supplierId: source.supplierId,
      shipTo: component.shipTo,
      unitPrice: source.unitPrice,
      value: bucket.plannedQty * source.unitPrice,
      needDay: bucket.needDay,
      releaseDay: bucket.releaseDay,
      late: isLate(bucket.releaseDay),
      isAlternate: source.isAlternate,
      contractPriced:
        !source.isAlternate && component.priceClauseId !== undefined,
    });
  });

  const windowGross = sumBuckets(gross.slice(0, RISK_WINDOW_BUCKETS));
  const windowReceipts = sumBuckets(
    component.receipts.slice(0, RISK_WINDOW_BUCKETS),
  );
  const windowShortfall = component.hold
    ? Math.max(0, windowGross - component.onHand - windowReceipts)
    : 0;
  const holdActive = component.hold !== undefined && !swapApproved;
  const lateOrders = orders.filter((order) => order.late).length;

  const riskReasons: string[] = [];
  if (holdActive) riskReasons.push('supply on quality hold');
  if (lateOrders > 0) {
    riskReasons.push(
      lateOrders === 1
        ? 'planned release past due'
        : 'planned releases past due',
    );
  }

  return {
    component,
    gross,
    buckets,
    sourcing,
    orders,
    plannedQty: sumBuckets(orders.map((order) => order.qty)),
    plannedValue: sumBuckets(orders.map((order) => order.value)),
    alternatePremium: sumBuckets(
      orders
        .filter((order) => order.isAlternate)
        .map((order) => order.qty * (order.unitPrice - component.unitPrice)),
    ),
    lateOrders,
    windowShortfall,
    holdActive,
    atRisk: riskReasons.length > 0,
    riskReasons,
  };
}

export interface OrderGroup {
  key: string;
  supplierId: SupplierId;
  shipTo: SiteId;
  orders: PlannedOrder[];
  value: number;
}

/** One group per supplier and ship-to pair, the way the buyer board carts are keyed. */
export function groupOrders(orders: PlannedOrder[]): OrderGroup[] {
  const groups = new Map<string, OrderGroup>();
  for (const order of orders) {
    const key = `${order.supplierId}|${order.shipTo}`;
    const group = groups.get(key);
    if (group) {
      group.orders.push(order);
      group.value += order.value;
    } else {
      groups.set(key, {
        key,
        supplierId: order.supplierId,
        shipTo: order.shipTo,
        orders: [order],
        value: order.value,
      });
    }
  }
  return [...groups.values()];
}

export interface BomModel {
  forecast: FgForecast;
  swapApproved: boolean;
  totalBuilds: number;
  plans: ComponentPlan[];
  orders: PlannedOrder[];
  /** Orders releasing within the window (including late ones): what gets sent. */
  releasable: PlannedOrder[];
  groups: OrderGroup[];
  plannedValue: number;
  componentsPlanned: number;
  atRiskComponents: ComponentPlan[];
  /** Panels (MCC and PDP) with no compliant breaker in the next 8 weeks; 0 once swapped. */
  panelsAtRisk: number;
  /** The same figure ignoring the swap, for "cleared risk on N panels". */
  heldPanels: number;
  /** Total premium paid on alternate-sourced orders. */
  alternatePremium: number;
  /** Quantity ordered from the alternate. */
  alternateQty: number;
}

export function buildBomModel(
  forecast: FgForecast,
  swapApproved: boolean,
): BomModel {
  const plans = COMPONENTS.map((component) =>
    planComponent(component, forecast, swapApproved),
  );
  const orders = plans.flatMap((plan) => plan.orders);
  const releasable = orders.filter(
    (order) => order.releaseDay <= RELEASE_WINDOW_DAYS,
  );
  const heldPanels = sumBuckets(plans.map((plan) => plan.windowShortfall));
  return {
    forecast,
    swapApproved,
    totalBuilds: totalBuilds(forecast),
    plans,
    orders,
    releasable,
    groups: groupOrders(releasable),
    plannedValue: sumBuckets(orders.map((order) => order.value)),
    componentsPlanned: plans.filter((plan) => plan.orders.length > 0).length,
    atRiskComponents: plans.filter((plan) => plan.atRisk),
    panelsAtRisk: swapApproved ? 0 : heldPanels,
    heldPanels,
    alternatePremium: sumBuckets(plans.map((plan) => plan.alternatePremium)),
    alternateQty: sumBuckets(
      orders.filter((order) => order.isAlternate).map((order) => order.qty),
    ),
  };
}

export function getPlan(model: BomModel, sku: ComponentSku): ComponentPlan {
  const found = model.plans.find((plan) => plan.component.sku === sku);
  if (!found) throw new Error(`No plan for ${sku}`);
  return found;
}

/** Share of the annual drive volume the panel shop consumes, as a percent. */
export function annualizedShare(
  sixMonthQty: number,
  annualVolume: number,
): number {
  if (annualVolume <= 0) return 0;
  return ((sixMonthQty * 2) / annualVolume) * 100;
}

export interface FlowLink {
  fg: FgSku;
  component: ComponentSku;
  /** Six-month component demand value, USD. */
  value: number;
  qty: number;
}

/** Finished good to component demand value over six months, at current prices. */
export function flowLinks(forecast: FgForecast): FlowLink[] {
  return BOM_LINES.map((line) => {
    const component = COMPONENTS.find((item) => item.sku === line.component);
    const qty = sumBuckets(forecast[line.fg]) * line.qtyPer;
    return {
      fg: line.fg,
      component: line.component,
      qty,
      value: qty * (component?.unitPrice ?? 0),
    };
  });
}

/** Where-used share: each finished good's slice of one component's six-month demand. */
export function whereUsedShares(
  component: ComponentSku,
  forecast: FgForecast,
): { fg: FgSku; qtyPer: number; qty: number; share: number }[] {
  const lines = BOM_LINES.filter((line) => line.component === component);
  const rows = lines.map((line) => ({
    fg: line.fg,
    qtyPer: line.qtyPer,
    qty: sumBuckets(forecast[line.fg]) * line.qtyPer,
  }));
  const total = sumBuckets(rows.map((row) => row.qty));
  return rows.map((row) => ({
    ...row,
    share: total > 0 ? row.qty / total : 0,
  }));
}

/**
 * Weeks of risk-window demand that usable stock and good open POs cover, for a
 * component with a quality hold. Demand runs at the window's average weekly rate.
 */
export function coverWeeks(plan: ComponentPlan): number {
  const windowGross = sumBuckets(plan.gross.slice(0, RISK_WINDOW_BUCKETS));
  const windowDays =
    BUCKET_START_DAYS[RISK_WINDOW_BUCKETS] - BUCKET_START_DAYS[0];
  const weeklyDemand = windowGross / (windowDays / DAYS_PER_WEEK);
  if (weeklyDemand <= 0) return 0;
  const supply =
    plan.component.onHand +
    sumBuckets(plan.component.receipts.slice(0, RISK_WINDOW_BUCKETS));
  return supply / weeklyDemand;
}
